
import React from 'react';
import { useTranslation, Trans } from 'react-i18next';

/**
 * A wrapper around the useTranslation hook that provides better type safety
 */
export const useTypedTranslation = () => {
  const { t, i18n } = useTranslation();
  return { t, i18n };
};

/**
 * A component for safely rendering translated content with HTML
 */
export const SafeTrans: React.FC<{ i18nKey: string; values?: Record<string, any> }> = ({ 
  i18nKey, 
  values 
}) => {
  return <Trans i18nKey={i18nKey} values={values}>{i18nKey}</Trans>;
};

/**
 * Helper function to safely get a translation with fallback handling
 */
export const getTranslation = (key: string, i18n: any, values?: Record<string, any>) => {
  try {
    return i18n.t(key, values) || key; // Add fallback to key if translation is empty
  } catch (error) {
    console.error(`Translation error for key: ${key}`, error);
    return key;
  }
};

/**
 * Helper for conditionally applying RTL direction for languages like Arabic
 */
export const getTextDirection = (language: string) => {
  const rtlLanguages = ['ar', 'he', 'ur', 'fa'];
  return rtlLanguages.includes(language) ? 'rtl' : 'ltr';
};

/**
 * Get current language name
 */
export const getLanguageName = (code: string): string => {
  const languages: Record<string, string> = {
    'en': 'English',
    'de': 'Deutsch',
    'ar': 'العربية',
    'tr': 'Türkçe',
    'ku': 'Kurdî'
  };
  return languages[code] || code;
};

/**
 * Check if a translation exists for a key
 */
export const hasTranslation = (key: string, i18n: any): boolean => {
  try {
    const translation = i18n.t(key);
    return translation !== key && !!translation;
  } catch (error) {
    return false;
  }
};

/**
 * Format a date according to the current language
 */
export const formatDate = (date: Date | string, i18n: any, options?: Intl.DateTimeFormatOptions): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  try {
    return new Intl.DateTimeFormat(i18n.language, options).format(dateObj);
  } catch (error) {
    console.error('Date formatting error:', error);
    return String(dateObj);
  }
};

/**
 * Handle pluralization with count
 */
export const getPlural = (key: string, count: number, i18n: any): string => {
  try {
    return i18n.t(key, { count });
  } catch (error) {
    console.error(`Plural translation error for key: ${key}`, error);
    return key;
  }
};
